import L from 'leaflet';
import { nzChartLayerGroup } from '../map/nzCharts';
import { setSearchPatternLayerOpacity, getSearchPatternLayerOpacity, hasSearchPatternLayers } from '../map/searchPatterns';

export function initChartLayersPanel(map: L.Map) {
    // 1. Inject HTML
    const panelHtml = `
<div id="chart-layers-panel" class="leaflet-control leaflet-bar" style="display:none; background-color: rgba(255, 255, 255, 0.95); color: #333; padding: 1em; width: auto; max-width: 95%; min-width: 300px; margin: 0; overflow: auto; max-height: 80vh; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 1000; resize: both;">
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1em; border-bottom: 2px solid #333; padding-bottom: 0.5em;">
        <h3 style="margin: 0; flex-grow: 1; text-align: center; color: #333;">Layer Opacity</h3>
        <button id="close-chart-layers-btn">Close</button>
    </div>

    <div id="psp-opacity-section" style="display:none; margin-bottom: 1em; border:1px solid #333; border-radius:8px; padding:1em;" >
        <h4>Search Pattern Opacity</h4>
        <div id="psp-opacity-sliders">
            <!-- Sliders will be injected here -->
        </div>
    </div>

    <div id="chart-opacity-section" style="display:none; border:1px solid #333; border-radius:8px; padding:1em; max-height: 300px; overflow-y: auto;">
        <h4>Active Charts Opacity</h4>
        <div id="chart-list-container">
            <!-- Sliders will be injected here -->
        </div>
        <button id="reset-chart-layers-btn" style="margin-top: 1em;">Reset Chart Opacity</button>
    </div>
</div>
    `;

    const appContainer = document.getElementById('app');
    if (appContainer) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = panelHtml;
        appContainer.appendChild(tempDiv.firstElementChild!);
    }

    // 2. Get References
    const panel = document.getElementById('chart-layers-panel')!;
    const chartListContainer = document.getElementById('chart-list-container')!;
    const pspOpacityContainer = document.getElementById('psp-opacity-sliders')!;
    const pspSection = document.getElementById('psp-opacity-section')!;
    const chartSection = document.getElementById('chart-opacity-section')!;
    const closeBtn = document.getElementById('close-chart-layers-btn')!;
    const resetBtn = document.getElementById('reset-chart-layers-btn')!;

    // 3. Logic
    function createSlider(label: string, type: string, defaultOpacity: number, updateFn: (type: string, val: number) => void, getFn: (type: string) => number | null) {
        const currentOpacity = getFn(type) ?? defaultOpacity;

        const row = document.createElement('div');
        row.style.marginBottom = '0.8em';
        
        const header = document.createElement('div');
        header.style.display = 'flex';
        header.style.justifyContent = 'space-between';
        
        const nameLabel = document.createElement('span');
        nameLabel.textContent = label;
        nameLabel.style.fontWeight = 'bold';
        nameLabel.style.fontSize = '0.9em';

        const valueLabel = document.createElement('span');
        valueLabel.textContent = `${Math.round(currentOpacity * 100)}%`;
        valueLabel.style.fontSize = '0.8em';

        const slider = document.createElement('input');
        slider.type = 'range';
        slider.min = '0';
        slider.max = '1';
        slider.step = '0.05';
        slider.value = currentOpacity.toString();
        slider.style.width = '100%';
        slider.style.marginTop = '0.2em';

        slider.addEventListener('input', () => {
            const val = parseFloat(slider.value);
            updateFn(type, val);
            valueLabel.textContent = `${Math.round(val * 100)}%`;
        });

        header.appendChild(nameLabel);
        header.appendChild(valueLabel);
        row.appendChild(header);
        row.appendChild(slider);
        return row;
    }

    function renderPspOpacitySliders() {
        pspOpacityContainer.innerHTML = '';
        if (hasSearchPatternLayers()) {
            pspSection.style.display = 'block';
            pspOpacityContainer.appendChild(createSlider('Ground Track', 'ground-track', 0.8, setSearchPatternLayerOpacity, getSearchPatternLayerOpacity));
            pspOpacityContainer.appendChild(createSlider('Ground BSD', 'ground-bsd', 0.3, setSearchPatternLayerOpacity, getSearchPatternLayerOpacity));
            pspOpacityContainer.appendChild(createSlider('Surface BSD', 'surface-bsd', 0.4, setSearchPatternLayerOpacity, getSearchPatternLayerOpacity));
        } else {
            pspSection.style.display = 'none';
        }
    }

    function updateChartList() {
        chartListContainer.innerHTML = '';
        const layers = nzChartLayerGroup.getLayers() as L.TileLayer[];
        
        layers.sort((a, b) => (b.options.zIndex || 0) - (a.options.zIndex || 0));

        if (layers.length > 0) {
            chartSection.style.display = 'block';
            layers.forEach(layer => {
                const name = (layer.options as any).chartName || `Chart ${(layer.options as any).layerId}`;
                const updateFn = (id: string, val: number) => {
                    const targetLayer = layers.find(l => ((l.options as any).chartName || `Chart ${(l.options as any).layerId}`) === id);
                    if (targetLayer) {
                        targetLayer.setOpacity(val);
                        targetLayer.options.opacity = val;
                    }
                };
                const getFn = (id: string) => {
                    const targetLayer = layers.find(l => ((l.options as any).chartName || `Chart ${(l.options as any).layerId}`) === id);
                    return targetLayer?.options.opacity ?? 1;
                };
                chartListContainer.appendChild(createSlider(name, name, 1, updateFn, getFn));
            });
        } else {
            chartSection.style.display = 'none';
        }
    }

    // 4. Attach Listeners
    map.getContainer().addEventListener('open-chart-layers-panel', () => {
        if (panel.style.display === 'block') {
            panel.style.display = 'none';
        } else {
            map.getContainer().dispatchEvent(new CustomEvent('close-other-panels', { detail: 'chart-layers-panel' }));
            updateChartList();
            renderPspOpacitySliders();
            panel.style.display = 'block';
        }
    });

    map.getContainer().addEventListener('close-other-panels', (e: any) => {
        if (e.detail !== 'chart-layers-panel') {
            panel.style.display = 'none';
        }
    });

    map.getContainer().addEventListener('nz-charts-updated', () => {
        if (panel.style.display === 'block') updateChartList();
    });
    
    map.getContainer().addEventListener('search-pattern-updated', () => {
        if (panel.style.display === 'block') renderPspOpacitySliders();
    });

    closeBtn.addEventListener('click', () => {
        panel.style.display = 'none';
    });

    resetBtn.addEventListener('click', () => {
        const layers = nzChartLayerGroup.getLayers() as L.TileLayer[];
        layers.forEach(layer => {
            layer.setOpacity(1);
            layer.options.opacity = 1;
        });
        updateChartList();
    });
}
